import $ from "jquery";

// 'use strict';

const ADDRESS_WIDGETS = {
  billing: null,
  shipping: null,
};

const LAST_SELECTED_COUNTRY = {
  billing: null,
  shipping: null,
};

function hasGooglePlacesApi() {
  return typeof google !== "undefined" && !!google.maps?.places;
}

function hasAddressAutocompleteFields() {
  return (
    !!document.querySelector("form.checkout") &&
    ["billing", "shipping"].some((prefix) => !!el(prefix, "address_1"))
  );
}

function ensureGooglePlacesHelpers() {
  if (window.eaplusOnGooglePlacesReady && window.eaplusLoadGooglePlacesScript) {
    return;
  }

  window.eaplusGooglePlacesCallbacks = window.eaplusGooglePlacesCallbacks || [];

  window.eaplusOnGooglePlacesReady = function eaplusOnGooglePlacesReady(callback) {
    if (hasGooglePlacesApi()) {
      callback();
      return;
    }

    window.eaplusGooglePlacesCallbacks.push(callback);
  };

  window.eaplusGooglePlacesReady = function eaplusGooglePlacesReady() {
    const callbacks = window.eaplusGooglePlacesCallbacks || [];

    while (callbacks.length) {
      callbacks.shift()();
    }
  };

  window.eaplusLoadGooglePlacesScript = function eaplusLoadGooglePlacesScript() {
    if (
      hasGooglePlacesApi() ||
      window.eaplusGooglePlacesRequested ||
      typeof EAPLUS_VARS === "undefined" ||
      !EAPLUS_VARS.google_script_url
    ) {
      return;
    }

    window.eaplusGooglePlacesRequested = true;

    const googleScriptElement = document.createElement("script");
    googleScriptElement.id = "eaplus-google-places-api";
    googleScriptElement.async = true;
    googleScriptElement.defer = true;
    googleScriptElement.src = `${EAPLUS_VARS.google_script_url}&callback=window.eaplusGooglePlacesReady`;
    document.head.appendChild(googleScriptElement);
  };
}

/**
 * Helper: safely get an element by prefix + field key
 *   ex: el('billing', 'country') -> #billing_country
 */
function el(prefix, key) {
  return document.getElementById(`${prefix}_${key}`);
}

/**
 * Initialize one autocomplete widget (billing|shipping) on its street address input.
 */
function initAutocompleteFor(prefix) {
  const streetElement = el(prefix, "address_1");
  if (!streetElement) {
    return;
  }

  if (ADDRESS_WIDGETS[prefix]) {
    // Already created; skip.
    return;
  }

  streetElement.addEventListener("keydown", function (event) {
    if (event.keyCode === 13) {
      event.preventDefault();
    }
  });

  let defaultCountryCode = el(prefix, "country")?.value;
  if (!defaultCountryCode) {
    defaultCountryCode = EAPLUS_VARS.defaultCountry;
  }

  const options = {
    componentRestrictions: { country: defaultCountryCode },
    fields: ["address_components"],
    strictBounds: false,
    types: ["address"],
  };

  const browserAutofillToken = streetElement.getAttribute("autocomplete");

  ADDRESS_WIDGETS[prefix] = new google.maps.places.Autocomplete(
    streetElement,
    options
  );

  if (browserAutofillToken) {
    keepAutocompleteToken(streetElement, browserAutofillToken);
  }

  ADDRESS_WIDGETS[prefix].addListener("place_changed", function () {
    populateAddressFromAutocomplete(prefix);
  });

  setAddressLookupCountryRestriction(prefix);
}

function keepAutocompleteToken(input, token) {
  const restore = () => {
    if (input.getAttribute("autocomplete") !== token) {
      input.setAttribute("autocomplete", token);
    }
  };

  new MutationObserver(restore).observe(input, {
    attributes: true,
    attributeFilter: ["autocomplete"],
  });
  restore();
}

/**
 * Called by Google script once it loads. Sets up billing/shipping if present.
 */
window.createAddressAutocomplete = function createAddressAutocomplete() {
  initAutocompleteFor("billing");
  initAutocompleteFor("shipping");
};

/**
 * Main entry point. Inject Google script and wire checkout updates.
 */
$(window).on("load", function () {
  if (typeof EAPLUS_VARS === "undefined" || !hasAddressAutocompleteFields()) {
    return;
  }

  ensureGooglePlacesHelpers();

  window.eaplusOnGooglePlacesReady(window.createAddressAutocomplete);
  window.eaplusLoadGooglePlacesScript();

  $(document.body).on("updated_checkout", function () {
    setAddressLookupCountryRestriction("billing");
    setAddressLookupCountryRestriction("shipping");
  });

  // Initialize shipping autocomplete when the "ship to different address" checkbox is checked.
  $("#ship-to-different-address-checkbox").on("change", function () {
    if ($(this).is(":checked") && typeof google !== "undefined") {
      initAutocompleteFor("shipping");
    }
  });
});

/**
 * Update componentRestrictions for a given prefix, if widget and country present.
 */
function setAddressLookupCountryRestriction(prefix) {
  const widget = ADDRESS_WIDGETS[prefix];
  if (!widget) return;

  let newCountryCode = el(prefix, "country")?.value;
  if (!newCountryCode) {
    newCountryCode = EAPLUS_VARS.default_country;
  }

  if (LAST_SELECTED_COUNTRY[prefix] !== newCountryCode) {
    LAST_SELECTED_COUNTRY[prefix] = newCountryCode;
    widget.setComponentRestrictions({ country: newCountryCode });
  }
}

/**
 * Get the current selected country code for a prefix, falling back to default.
 */
function getAddressAutocompleteCountryCode(prefix) {
  let countryCode = el(prefix, "country")?.value;
  if (!countryCode) {
    countryCode = EAPLUS_VARS.default_country;
  }
  return countryCode;
}

/**
 * Reset address fields for a given prefix.
 */
function resetAddress(prefix) {
  const fields = [
    `${prefix}_address_1`,
    // `${prefix}_address_2`, // intentionally skipped as in original
    `${prefix}_postcode`,
    `${prefix}_city`,
    `${prefix}_state`,
  ];

  fields.forEach((id) => {
    const input = document.getElementById(id);
    if (input) input.value = "";
  });

  $(`#${prefix}_state`).change();
}

/**
 * Extract a component from a Google Place object.
 */
function getAddressComponent(place, componentType, componentLength = "short_name") {
  let componentValue = null;
  for (const component of place.address_components) {
    if (component.types[0] === componentType) {
      componentValue = component[componentLength];
      break;
    }
  }
  if (componentValue == null) return "";

  if (componentType === "street_number") {
    return componentValue + " ";
  }
  return componentValue;
}

/**
 * Pick best postal code and set it for a given prefix.
 */
function setBestPostalCode(prefix, place) {
  const postalComponents = ["postal_code", "postal_code_suffix", "postal_code_prefix"];
  const input = el(prefix, "postcode");
  if (!input) return;

  postalComponents.forEach((pc) => {
    if (input.value) return; // already set
    const val = getAddressComponent(place, pc);
    if (val) input.value = val;
  });
}

/**
 * Try setting the state select value; fallback to matching by option text.
 */
function setState(prefix, state) {
  const selectElement = el(prefix, "state");
  if (!selectElement) return;

  selectElement.value = state;

  if (state && !selectElement.value) {
    Array.from(selectElement.options).forEach((opt) => {
      if (opt.text && opt.text === state) {
        selectElement.value = opt.value;
      }
    });
  }
}

/**
 * Handle place selection for a given prefix (billing|shipping).
 */
function populateAddressFromAutocomplete(prefix) {
  const widget = ADDRESS_WIDGETS[prefix];
  if (!widget) return;

  const place = widget.getPlace();
  if (!place || !place.address_components) {
    return;
  }

  resetAddress(prefix);

  const countryCode = getAddressAutocompleteCountryCode(prefix);
  let sublocality = null;

  if (countryCode === "US") {
    const stateShort = getAddressComponent(place, "administrative_area_level_1", "short_name");
    if ($.inArray(stateShort, EAPLUS_VARS.excluded_states) >= 0) {
      jQuery("#checkout-notice-state").modal("show");
      resetAddress(prefix);
      return;
    }

    el(prefix, "address_1").value =
      getAddressComponent(place, "street_number") +
      getAddressComponent(place, "route", "long_name");
    el(prefix, "city").value = getAddressComponent(place, "locality");
    if (!el(prefix, "city").value) {
      el(prefix, "city").value = getAddressComponent(place, "sublocality_level_1");
    }
    if (!el(prefix, "city").value) {
      el(prefix, "city").value = getAddressComponent(place, "administrative_area_level_2", "long_name");
    }
    el(prefix, "postcode").value = getAddressComponent(place, "postal_code");
    el(prefix, "state").value = getAddressComponent(
      place,
      "administrative_area_level_1"
    );
  } else if (countryCode === "GB") {
    el(prefix, "address_1").value =
      getAddressComponent(place, "street_number") +
      getAddressComponent(place, "route", "long_name");

    el(prefix, "city").value = getAddressComponent(place, "locality");
    if (!el(prefix, "city").value) {
      el(prefix, "city").value = getAddressComponent(place, "postal_town");
    }

    el(prefix, "state").value = getAddressComponent(
      place,
      "administrative_area_level_2",
      "long_name"
    );

    setBestPostalCode(prefix, place);
  } else {
    // Generic handler
    for (const component of place.address_components) {
      const type = component.types[0];

      switch (type) {
        case "street_number":
          el(prefix, "address_1").value = component.long_name;
          break;

        case "route":
          el(prefix, "address_1").value += " " + component.short_name;
          break;

        case "sublocality_level_1":
          sublocality = component.long_name;
          break;

        case "locality":
          // el(prefix, "address_2").value = component.long_name; // intentionally not used
          el(prefix, "city").value = component.long_name;
          break;

        case "postal_town":
          el(prefix, "city").value = component.long_name;
          break;

        case "administrative_area_level_1":
          setState(prefix, component.short_name);
          break;

        default:
          // ignore
          break;
      }
    }

    setBestPostalCode(prefix, place);
  }

  // If state is still empty, try level 2 (counties, etc.)
  if (!el(prefix, "state").value?.length) {
    el(prefix, "state").value = getAddressComponent(
      place,
      "administrative_area_level_2"
    );
  }

  $(`#${prefix}_address_1`).trigger("change");

  // Trigger select refresh if present
  if (el(prefix, "state").value) {
    $(`#${prefix}_state`).change();
  }

  // Re-validate WC required fields
  jQuery(".validate-required input, .validate-required select").trigger("validate");
}
